import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Users, Search, Filter, Settings, ExternalLink, Check, X, Zap, CheckSquare, Square, MinusSquare, Cog } from 'lucide-react'
import { adminApi } from '../services/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import TenantFeatureToggle from '../components/TenantFeatureToggle'
import FeatureAssignmentModal from '../components/FeatureAssignmentModal'
import BulkFeatureAssignmentModal from '../components/BulkFeatureAssignmentModal'
import TenantSettingsModal from '../components/TenantSettingsModal'

const Tenants = () => {
  const [tenants, setTenants] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTenant, setEditingTenant] = useState(null)
  const [featureToggleTenant, setFeatureToggleTenant] = useState(null)
  const [featureAssignmentTenant, setFeatureAssignmentTenant] = useState(null)
  const [settingsTenant, setSettingsTenant] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Bulk selection states
  const [selectedTenants, setSelectedTenants] = useState([])
  const [showBulkFeatureModal, setShowBulkFeatureModal] = useState(false)
  const [bulkOperation, setBulkOperation] = useState('assign')

  useEffect(() => {
    fetchTenants()
  }, [currentPage, searchTerm, filterStatus])

  const fetchTenants = async () => {
    try {
      setIsLoading(true)
      const response = await adminApi.getTenants(currentPage, 10, searchTerm)
      setTenants(response.data.tenants || [])
      setTotalPages(response.data.totalPages || 1)
    } catch (error) {
      console.error('Error fetching tenants:', error)
      toast.error('Failed to load tenants')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateTenant = async (formData) => {
    try {
      await adminApi.createTenant(formData)
      toast.success('Tenant created successfully')
      setShowCreateModal(false)
      fetchTenants()
    } catch (error) {
      console.error('Error creating tenant:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to create tenant'
      toast.error(errorMessage)
    }
  }

  const handleUpdateTenant = async (id, formData) => {
    try {
      await adminApi.updateTenant(id, formData)
      toast.success('Tenant updated successfully')
      setEditingTenant(null)
      fetchTenants()
    } catch (error) {
      console.error('Error updating tenant:', error)
      toast.error('Failed to update tenant')
    }
  }

  const handleDeleteTenant = async (id) => {
    if (!confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return
    }

    try {
      await adminApi.deleteTenant(id)
      toast.success('Tenant deleted successfully')
      fetchTenants()
    } catch (error) {
      console.error('Error deleting tenant:', error)
      toast.error('Failed to delete tenant')
    }
  }

  const handleFeatureAssignment = async (tenantId, featureIds) => {
    try {
      if (!tenantId) {
        console.error('Tenant ID is missing')
        toast.error('Tenant ID is missing')
        return
      }
      await adminApi.assignFeatures(tenantId, featureIds)
      toast.success('Features assigned successfully')
      setFeatureAssignmentTenant(null)
      fetchTenants()
    } catch (error) {
      console.error('Error assigning features:', error)
      toast.error(error.response?.data?.message || 'Failed to assign features')
    }
  }

  const handleSettingsUpdate = () => {
    fetchTenants()
  }

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || tenant.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const statuses = ['all', 'trial', 'active', 'suspended', 'expired']

  if (isLoading) {
    return (
      <div className="page-container animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white p-4 rounded-lg shadow-sm">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    )
  }

  const handleSelectAll = () => {
    const filteredTenantIds = filteredTenants.map(t => t.id)
    const allSelected = filteredTenantIds.every(id => selectedTenants.includes(id))

    if (allSelected) {
      setSelectedTenants(prev => prev.filter(id => !filteredTenantIds.includes(id)))
    } else {
      setSelectedTenants(prev => [...new Set([...prev, ...filteredTenantIds])])
    }
  }

  const handleBulkFeatureAssignment = (operation) => {
    setBulkOperation(operation)
    setShowBulkFeatureModal(true)
  }

  const handleBulkOperationComplete = () => {
    setSelectedTenants([])
    setShowBulkFeatureModal(false)
    fetchTenants()
  }

  const getSelectedTenantObjects = () => {
    return tenants.filter(tenant => selectedTenants.includes(tenant.id))
  }

  const getSelectAllState = () => {
    const filteredTenantIds = filteredTenants.map(t => t.id)
    const selectedCount = filteredTenantIds.filter(id => selectedTenants.includes(id)).length

    if (selectedCount === 0) return 'none'
    if (selectedCount === filteredTenantIds.length) return 'all'
    return 'partial'
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Müşteriler</h1>
          <p className="page-subtitle">Sisteme kayıtlı firmaların yönetimi</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <Plus size={20} className="mr-2" />
          Firma Ekle
        </button>
      </div>

      {/* Search and Filter */}
      <div className="glass-card mb-6 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Firma ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>

          <div className="flex gap-2">
            <div className="relative">
              <Filter size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input pl-10"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="active">Aktif</option>
                <option value="trial">Deneme</option>
                <option value="suspended">Askıya Alınmış</option>
                <option value="expired">Süresi Bitmiş</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedTenants.length > 0 && (
        <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-primary-800">
                {selectedTenants.length} firma seçildi
              </span>
              <button
                onClick={() => setSelectedTenants([])}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Seçimi Temizle
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkFeatureAssignment('assign')}
                className="btn btn-sm btn-primary"
              >
                <Plus size={16} className="mr-1" />
                Özellik Ata
              </button>
              <button
                onClick={() => handleBulkFeatureAssignment('unassign')}
                className="btn btn-sm btn-secondary"
              >
                <X size={16} className="mr-1" />
                Özellik Kaldır
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tenants List */}
      <div className="table-container">
        <table className="modern-table">
          <thead>
            <tr>
              <th className="w-12">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center justify-center w-4 h-4 text-gray-500 hover:text-gray-700"
                >
                  {getSelectAllState() === 'all' ? (
                    <CheckSquare size={16} className="text-primary-600" />
                  ) : getSelectAllState() === 'partial' ? (
                    <MinusSquare size={16} className="text-primary-600" />
                  ) : (
                    <Square size={16} />
                  )}
                </button>
              </th>
              <th>Firma Adı</th>
              <th>Alan Adı (Subdomain)</th>
              <th>Durum</th>
              <th>Paket</th>
              <th>Kullanıcılar</th>
              <th>Kayıt Tarihi</th>
              <th className="text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.map((tenant) => (
              <tr key={tenant.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedTenants.includes(tenant.id)}
                    onChange={() => handleSelectTenant(tenant.id)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </td>
                <td>
                  <div>
                    <div className="font-medium text-secondary-900">{tenant.name}</div>
                    <div className="text-sm text-secondary-500">{tenant.email}</div>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <code className="px-2 py-1 bg-gray-100 rounded text-xs text-secondary-600 border border-gray-200">
                      {tenant.subdomain}
                    </code>
                    <a
                      href={`https://${tenant.subdomain}.stoklyapp.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </td>
                <td>
                  <span className={`badge ${tenant.status === 'active' ? 'badge-success' :
                      tenant.status === 'trial' ? 'badge-info' :
                        tenant.status === 'suspended' ? 'badge-danger' :
                          'badge-neutral'
                    }`}>
                    {tenant.status === 'active' ? 'Aktif' :
                      tenant.status === 'trial' ? 'Deneme' :
                        tenant.status === 'suspended' ? 'Askıda' : 'Süresi Doldu'}
                  </span>
                </td>
                <td>
                  <span className="capitalize text-sm text-secondary-700">{tenant.subscriptionPlan}</span>
                </td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <Users size={14} className="text-secondary-400" />
                    <span className="text-sm text-secondary-700 font-medium">{tenant.userCount || 0}</span>
                    <span className="text-xs text-secondary-400">/ {tenant.maxUsers}</span>
                  </div>
                </td>
                <td>
                  <div className="text-sm text-secondary-500">
                    {format(new Date(tenant.createdAt), 'dd MMM yyyy')}
                  </div>
                </td>
                <td>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setSettingsTenant(tenant)}
                      className="btn btn-sm btn-ghost"
                      title="Firma Ayarları"
                    >
                      <Cog size={16} />
                    </button>
                    <button
                      onClick={() => setFeatureToggleTenant(tenant)}
                      className="btn btn-sm btn-ghost"
                      title="Özellik Yönetimi"
                    >
                      <Settings size={16} />
                    </button>
                    <button
                      onClick={() => setFeatureAssignmentTenant(tenant)}
                      className="btn btn-sm btn-ghost text-primary-600 hover:bg-primary-50"
                      title="Hızlı Özellik Ata"
                    >
                      <Zap size={16} />
                    </button>
                    <button
                      onClick={() => setEditingTenant(tenant)}
                      className="btn btn-sm btn-ghost"
                      title="Düzenle"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteTenant(tenant.id)}
                      className="btn btn-sm btn-ghost text-red-600 hover:bg-red-50"
                      title="Sil"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredTenants.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-secondary-500 font-medium">Kayıtlı firma bulunamadı</p>
            <p className="text-sm text-secondary-400 mt-1">Arama kriterlerinizi değiştirin veya yeni firma ekleyin</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn btn-sm btn-secondary disabled:opacity-50"
            >
              Önceki
            </button>
            <span className="text-sm text-secondary-600 px-2">
              Sayfa {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn btn-sm btn-secondary disabled:opacity-50"
            >
              Sonraki
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTenant) && (
        <TenantModal
          tenant={editingTenant}
          onClose={() => {
            setShowCreateModal(false)
            setEditingTenant(null)
          }}
          onSubmit={editingTenant ?
            (data) => handleUpdateTenant(editingTenant.id, data) :
            handleCreateTenant
          }
        />
      )}

      {/* Feature Toggle Modal */}
      <TenantFeatureToggle
        tenant={featureToggleTenant}
        isOpen={!!featureToggleTenant}
        onClose={() => setFeatureToggleTenant(null)}
      />

      {/* Feature Assignment Modal */}
      <FeatureAssignmentModal
        tenant={featureAssignmentTenant}
        isOpen={!!featureAssignmentTenant}
        onClose={() => setFeatureAssignmentTenant(null)}
        onAssign={handleFeatureAssignment}
      />

      {/* Bulk Feature Assignment Modal */}
      <BulkFeatureAssignmentModal
        tenants={getSelectedTenantObjects()}
        isOpen={showBulkFeatureModal}
        onClose={() => setShowBulkFeatureModal(false)}
        onComplete={handleBulkOperationComplete}
      />

      {/* Tenant Settings Modal */}
      <TenantSettingsModal
        tenant={settingsTenant}
        isOpen={!!settingsTenant}
        onClose={() => setSettingsTenant(null)}
        onUpdate={handleSettingsUpdate}
      />
    </div>
  )
}

const TenantModal = ({ tenant, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    subdomain: tenant?.subdomain || '',
    email: tenant?.email || '',
    phone: tenant?.phone || '',
    address: tenant?.address || '',
    status: tenant?.status || 'trial',
    subscriptionPlan: tenant?.subscriptionPlan || 'free',
    maxUsers: tenant?.maxUsers || 5,
    maxStorage: tenant?.maxStorage || 1073741824, // 1GB in bytes
    // Admin user fields (only for create)
    adminUser: tenant ? null : {
      firstName: '',
      lastName: '',
      email: '',
      password: ''
    },
    // Auto-setup options
    autoSetup: {
      assignFeaturesByPlan: true,
      createDefaultSettings: true,
      enableEmailNotifications: false
    }
  })

  const [features, setFeatures] = useState([])
  const [selectedFeatures, setSelectedFeatures] = useState([])

  // Load features for preview
  useEffect(() => {
    if (!tenant) { // Only for create mode
      const loadFeatures = async () => {
        try {
          const response = await adminApi.getFeatures()
          setFeatures(response.data.features || [])
        } catch (error) {
          console.error('Error loading features:', error)
        }
      }
      loadFeatures()
    }
  }, [tenant])

  // Update selected features based on subscription plan
  useEffect(() => {
    if (!tenant && formData.autoSetup.assignFeaturesByPlan) {
      const planFeatures = features.filter(feature => {
        if (feature.config?.autoAssign === false) return false
        const plan = formData.subscriptionPlan
        if (plan === 'free') return feature.requiredPlan === 'free'
        if (plan === 'basic') return ['free', 'basic'].includes(feature.requiredPlan)
        if (plan === 'professional') return ['free', 'basic', 'premium'].includes(feature.requiredPlan)
        if (plan === 'enterprise') return true
        return false
      })
      setSelectedFeatures(planFeatures)
    }
  }, [formData.subscriptionPlan, formData.autoSetup.assignFeaturesByPlan, features, tenant])

  const handleSubmit = (e) => {
    e.preventDefault()

    const submitData = {
      ...formData,
      maxStorage: parseInt(formData.maxStorage, 10),
      maxUsers: parseInt(formData.maxUsers, 10),
    }

    // Include adminUser and autoSetup only for create mode
    if (!tenant) {
      submitData.adminUser = formData.adminUser
      submitData.autoSetup = formData.autoSetup
    }

    onSubmit(submitData)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (name.startsWith('adminUser.')) {
      const field = name.replace('adminUser.', '')
      setFormData(prev => ({
        ...prev,
        adminUser: {
          ...prev.adminUser,
          [field]: value
        }
      }))
    } else if (name.startsWith('autoSetup.')) {
      const field = name.replace('autoSetup.', '')
      setFormData(prev => ({
        ...prev,
        autoSetup: {
          ...prev.autoSetup,
          [field]: type === 'checkbox' ? checked : value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {tenant ? 'Edit Tenant' : 'Create Tenant'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subdomain
              </label>
              <div className="flex">
                <input
                  type="text"
                  name="subdomain"
                  value={formData.subdomain}
                  onChange={handleChange}
                  className="input rounded-r-none"
                  required
                />
                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  .stoklyapp.com
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input"
              >
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subscription Plan
              </label>
              <select
                name="subscriptionPlan"
                value={formData.subscriptionPlan}
                onChange={handleChange}
                className="input"
              >
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Users
              </label>
              <input
                type="number"
                name="maxUsers"
                value={formData.maxUsers}
                onChange={handleChange}
                className="input"
                min="1"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Storage (GB)
              </label>
              <input
                type="number"
                name="maxStorage"
                value={Math.round(formData.maxStorage / (1024 * 1024 * 1024))}
                onChange={(e) => handleChange({
                  target: {
                    name: 'maxStorage',
                    value: e.target.value * 1024 * 1024 * 1024
                  }
                })}
                className="input"
                min="1"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
              className="input"
            />
          </div>

          {/* Admin User Section (Create Only) */}
          {!tenant && (
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Admin User Setup</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="adminUser.firstName"
                    value={formData.adminUser?.firstName || ''}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="adminUser.lastName"
                    value={formData.adminUser?.lastName || ''}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Email
                  </label>
                  <input
                    type="email"
                    name="adminUser.email"
                    value={formData.adminUser?.email || ''}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    name="adminUser.password"
                    value={formData.adminUser?.password || ''}
                    onChange={handleChange}
                    className="input"
                    required
                    minLength={8}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Auto-Setup Section (Create Only) */}
          {!tenant && (
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Auto-Setup Options</h4>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="assignFeaturesByPlan"
                    name="autoSetup.assignFeaturesByPlan"
                    checked={formData.autoSetup.assignFeaturesByPlan}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="assignFeaturesByPlan" className="ml-2 block text-sm text-gray-900">
                    Auto-assign features based on subscription plan
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="createDefaultSettings"
                    name="autoSetup.createDefaultSettings"
                    checked={formData.autoSetup.createDefaultSettings}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="createDefaultSettings" className="ml-2 block text-sm text-gray-900">
                    Create default tenant settings
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableEmailNotifications"
                    name="autoSetup.enableEmailNotifications"
                    checked={formData.autoSetup.enableEmailNotifications}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="enableEmailNotifications" className="ml-2 block text-sm text-gray-900">
                    Enable email notifications
                  </label>
                </div>
              </div>

              {/* Feature Preview */}
              {formData.autoSetup.assignFeaturesByPlan && selectedFeatures.length > 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">
                    Features to be assigned ({formData.subscriptionPlan} plan):
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedFeatures.map(feature => (
                      <div key={feature.id} className="flex items-center text-sm">
                        <Check size={16} className="text-green-600 mr-2" />
                        <span className="text-gray-700">{feature.name}</span>
                        <span className="ml-2 text-xs text-gray-500">({feature.category})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {tenant ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  const handleFeatureAssignment = () => {
    // Refresh the tenant list after feature assignment
    fetchTenants()
  }
}

export default Tenants 
